import {useCallback, useState} from 'react';
import {toMmsUrl, withParams} from '../../../../utils/imageUtils';
import {getEffectiveHash} from '../utils/categoryImageParser';
import {buildEditableParams} from '../utils/parameterUtils';

const getPreviewUrl = (
  formValues,
  styleCardImageUrl,
  categoryImages,
  primaryCategoryId,
  colors,
  refreshToken
) => {
  if (formValues.useLabImage && formValues) {
    // Construct lab image URL from form values
    // Example pattern: mms/images/catalog/{hash}/colors/{color_id}/views/alt/front_medium_extended.png
    const hash = getEffectiveHash(
      styleCardImageUrl,
      categoryImages,
      primaryCategoryId
    );

    // Use first color if colorId is null/empty, otherwise use specified colorId
    let effectiveColorId = formValues.colorId;

    // If no colorId is specified (null or empty), use the first available color
    if (!effectiveColorId && colors?.[0]?.id != null) {
      effectiveColorId = String(colors[0].id);
    }

    if (effectiveColorId) {
      const labImagePath = `mms/images/catalog/${hash}/colors/${effectiveColorId}/views/alt/front_medium_extended.png`;
      const params = buildEditableParams(formValues);

      return withParams(toMmsUrl(labImagePath), {...params, t: refreshToken});
    }
  }

  // For laydown images, use styleCardImageUrl directly
  if (styleCardImageUrl) {
    return withParams(toMmsUrl(styleCardImageUrl), {t: refreshToken});
  }

  return null;
};

/**
 * Hook for generating preview URLs on demand
 * @param {Object} config - Configuration object
 * @param {string} config.styleCardImageUrl - Style card image URL
 * @param {Object} config.formValues - Current form values for lab image construction
 * @param {Array} config.categoryImages - Array of category image objects
 * @param {Array} config.colors - Array of color objects
 * @param {string|number} config.primaryCategoryId - Primary category ID
 * @returns {Object} Preview URL (null until refreshPreview is called) and refresh functionality
 */
export default function usePreviewUrl({
  categoryImages,
  colors,
  getValues,
  primaryCategoryId,
  styleCardImageUrl
}) {
  const [previewToken, setPreviewToken] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const refreshPreviewToken = () => {
    const nextToken = String(Date.now());
    setPreviewToken(nextToken);
    setPreviewUrl(
      getPreviewUrl(
        getValues(),
        styleCardImageUrl,
        categoryImages,
        primaryCategoryId,
        colors,
        nextToken
      )
    );
  };

  const refreshPreview = useCallback(() => {
    setPreviewUrl(
      getPreviewUrl(
        getValues(),
        styleCardImageUrl,
        categoryImages,
        primaryCategoryId,
        colors,
        previewToken
      )
    );
  }, [
    getValues,
    styleCardImageUrl,
    categoryImages,
    colors,
    primaryCategoryId,
    previewToken
  ]);

  return {previewUrl, refreshPreview, refreshPreviewToken};
}
